import React, { useMemo } from 'react'
import '../styles/yearRangeSlider.css'

const MAX_LABELED_TICKS = 8

// Which tick indexes get a visible year label underneath them, so labels
// never crowd into each other on a series with many years — every index
// still gets a small tick mark on the rail, just not all of them get text.
function pickLabeledIndexes(count) {
  if (count <= MAX_LABELED_TICKS) {
    return new Set(Array.from({ length: count }, (_, i) => i))
  }
  const step = (count - 1) / (MAX_LABELED_TICKS - 1)
  const indexes = new Set()
  for (let i = 0; i < MAX_LABELED_TICKS; i++) {
    indexes.add(Math.round(i * step))
  }
  return indexes
}

// Dual-handle year range slider. Built on two overlapping native
// <input type="range"> elements (not a custom pointer-drag implementation)
// so dragging, click-to-jump, keyboard arrows, and touch all work for free
// — the styling makes both inputs' own tracks fully invisible and draws one
// shared rail + highlighted fill + tick marks underneath them instead, so
// nothing from one input's native track can paint over the other's thumb.
//
// Works on the *index* into `years` (not the year value itself), so it
// still behaves correctly when the available years have gaps (e.g. a
// series with missing years).
function YearRangeSlider({ years, fromYear, toYear, onChange, disabled }) {
  const maxIndex = (years?.length || 0) - 1

  const fromIndex = Math.max(0, years?.indexOf(Number(fromYear)) ?? -1)
  const toIndexRaw = years?.indexOf(Number(toYear)) ?? -1
  const toIndex = toIndexRaw === -1 ? maxIndex : toIndexRaw

  const labeledIndexes = useMemo(() => pickLabeledIndexes(years?.length || 0), [years?.length])

  if (!years || years.length < 2) return null

  const fromPercent = (fromIndex / maxIndex) * 100
  const toPercent = (toIndex / maxIndex) * 100

  const handleFromChange = (event) => {
    const index = Math.min(Number(event.target.value), toIndex)
    onChange(years[index], years[toIndex])
  }

  const handleToChange = (event) => {
    const index = Math.max(Number(event.target.value), fromIndex)
    onChange(years[fromIndex], years[index])
  }

  return (
    <div className={`year-range-slider${disabled ? ' year-range-slider-disabled' : ''}`}>
      <div className="year-range-slider-selected-range">
        {years[fromIndex]} – {years[toIndex]}
      </div>

      <div className="year-range-slider-track">
        <div className="year-range-slider-rail" />
        <div
          className="year-range-slider-fill"
          style={{ left: `${fromPercent}%`, width: `${toPercent - fromPercent}%` }}
        />

        <div className="year-range-slider-ticks">
          {years.map((year, index) => (
            <span
              key={year}
              className={`year-range-slider-tick${index >= fromIndex && index <= toIndex ? ' year-range-slider-tick-active' : ''}`}
              style={{ left: `${(index / maxIndex) * 100}%` }}
            />
          ))}
        </div>

        <input
          type="range"
          className="year-range-slider-input"
          min={0}
          max={maxIndex}
          value={fromIndex}
          onChange={handleFromChange}
          disabled={disabled}
          aria-label="From year"
        />
        <input
          type="range"
          className="year-range-slider-input"
          min={0}
          max={maxIndex}
          value={toIndex}
          onChange={handleToChange}
          disabled={disabled}
          aria-label="To year"
        />
      </div>

      <div className="year-range-slider-labels">
        {years.map((year, index) => (
          labeledIndexes.has(index) ? (
            <span
              key={year}
              className={`year-range-slider-label${index >= fromIndex && index <= toIndex ? ' year-range-slider-label-active' : ''}`}
              style={{ left: `${(index / maxIndex) * 100}%` }}
            >
              {year}
            </span>
          ) : null
        ))}
      </div>
    </div>
  )
}

export default YearRangeSlider
